import { useState } from 'react';
import { FieldError, FieldErrors, FieldValues } from 'react-hook-form';
import Button from './Button';
import { OptionProps, TOption } from './iInput';
import { InputName } from './Input';
import { OptionInput } from './OptionInput';
import { cn } from '@lib/utils';

type OptionInputInWrapper = Omit<OptionProps, 'inputName'>;

type InputWrapperProps<FormType extends FieldValues> = {
  label?: string;
  errors?: FieldErrors<FormType>;
  helpText?: string;
  inputProps: OptionInputInWrapper;
  labelClassName?: string;
  className?: string;
  optional?: boolean;
  optionalLabel?: string;
  defaultHideOptional?: boolean;
  resetMargin?: boolean;
};

function FormOptionWrapper<FormType extends FieldValues>({
  inputProps,
  helpText,
  className,
  label = '',
  labelClassName = '',
  errors = {} as FieldErrors<any>,
  optional = false,
  optionalLabel = 'Optional',
  defaultHideOptional = true,
  resetMargin,
}: InputWrapperProps<FormType>) {
  const [isFocused, setIsFocused] = useState(false);
  const [showOptional, setShowOptional] = useState(
    defaultHideOptional ? false : !!inputProps.value
  );
  const hasErrors = Object.prototype.hasOwnProperty.call(
    errors,
    inputProps.name
  );
  if (optional && !showOptional) {
    return (
      <div className={cn('md:mb-4', className)}>
        <Button
          className="inline-block"
          asText
          onClick={() => setShowOptional(true)}
        >
          {optionalLabel}
        </Button>
      </div>
    );
  }
  // const isForm = inputProps.input === InputName.Text || inputProps.input === InputName.Email
  //   || inputProps.input === InputName.Phone || inputProps.input === InputName.Password
  //   || inputProps.input === InputName.Number || inputProps.input === InputName.Decimal
  //   || inputProps.input === InputName.Date || inputProps.input === InputName.Checkbox;
  const isMaterial = false;
  const err: FieldError | undefined = (errors as FieldValues)[inputProps.name];
  // console.log('inputprops', inputProps.name, {...inputProps});
  return (
    <div
      className={cn(
        'input-wrapper',
        className,
        isMaterial && 'material',
        inputProps.disabled && 'disabled',
        !resetMargin && 'mb-4'
      )}
      data-error={hasErrors}
    >
      {label && (
        <label
          htmlFor={inputProps.name}
          className={cn(
            isFocused && 'active',
            typeof inputProps.value !== 'undefined' &&
              inputProps.value !== null &&
              inputProps.value?.toString().length > 0 &&
              'filled',
            !resetMargin && 'mb-2',
            labelClassName
          )}
        >
          {label}
        </label>
      )}

      {inputProps.input === 'radio' && (
        <div
          className={cn(
            'radio-group',
            inputProps.singleColumn && 'grid grid-cols-1'
          )}
        >
          {inputProps.options?.map(
            ({
              label: optionLabel,
              value: v,
              icon,
              className: optionInputClassName,
              labelClassName: optionLabelClassName,
              disabled,
            }: TOption) => {
              const optionName = `${inputProps.name}_${v}`;
              return (
                <div
                  key={v}
                  className={cn(
                    icon ? 'radio-icon' : 'radio',
                    optionInputClassName,
                    disabled && 'cursor-not-allowed'
                  )}
                >
                  <OptionInput
                    disabled={disabled}
                    isMaterial={isMaterial}
                    {...inputProps}
                    onFocus={() => [setIsFocused(true)]}
                    onClick={(e) => {
                      if (inputProps.onClick) {
                        inputProps.onClick(e);
                      }
                    }}
                    onChange={(e) => {
                      if (e === null || typeof e === 'undefined') {
                        if (inputProps.onChange) {
                          inputProps.onChange(e);
                        }
                        return;
                      }
                      if (inputProps.input === InputName.Checkbox) {
                        if (typeof e === 'object') {
                          if (inputProps.onChange) {
                            inputProps.onChange(e.target.checked);
                          }
                        } else {
                          if (inputProps.onChange) {
                            inputProps.onChange(e);
                          }
                        }

                        return;
                      }

                      if (inputProps.input === 'radio') {
                        // console.log('changed', inputProps.name, e);
                        if (typeof e === 'object') {
                          if (inputProps.onChange) {
                            inputProps.onChange(e.target.value);
                          }
                        } else {
                          if (inputProps.onChange) {
                            inputProps.onChange(e);
                          }
                        }

                        return;
                      }
                      if (typeof e === 'object' && !Array.isArray(e)) {
                        if (inputProps.onChange) {
                          inputProps.onChange(e.target.value);
                        }
                      } else {
                        if (inputProps.onChange) {
                          inputProps.onChange(e);
                        }
                      }
                    }}
                    onBlur={() => setIsFocused(false)}
                    name={`${inputProps.name}_${v}`}
                    inputName={inputProps.name}
                    value={v}
                    checked={inputProps.value === v}
                  />
                  <label
                    className={cn(
                      inputProps.value !== v && 'bg-white',
                      disabled
                        ? 'cursor-not-allowed text-slate-400'
                        : 'cursor-pointer'
                    )}
                    htmlFor={`${optionName}`}
                  >
                    {icon}
                    <span
                      className={`${optionLabelClassName} ${cn(
                        inputProps.value === v && 'font-bold'
                      )} ${disabled && 'cursor-not-allowed'}`}
                    >
                      {optionLabel}
                    </span>
                  </label>
                </div>
              );
            }
          )}
        </div>
      )}

      {helpText && <p className="input-text">{helpText}</p>}
      {hasErrors && <p className="input-text">{err?.message}</p>}
    </div>
  );
}

export default FormOptionWrapper;
